import { StepDirection } from 'scenes/common/actions/types';

export enum InsuranceName {
  Car = 'Car',
  Home = 'Home',
  Landlord = 'Landlord',
}

export type InsuranceInfoType = {
  name: InsuranceName;
  image: string;
  discount: number;
  saveOnlineText: string;
  links: {
    findOutMore: string;
    getQuote: string;
  };
  termsAndConditionsElement: JSX.Element;
  termsLinkText: string;
  termsModalHeading: string;
};

export enum InsuredItem {
  Car = 'car',
  Home = 'home',
  Property = 'property',
}

export enum ProgressBarFlow {
  GetQuote = 'Get Quote',
  PolicyDetails = 'Policy Details',
  BuyPolicy = 'Buy Policy',
}

export enum YesNoAnswer {
  Unspecified = '',
  Yes = 'Yes',
  No = 'No',
}

export const YES_NO_OPTIONS = [
  { label: YesNoAnswer.Yes, value: YesNoAnswer.Yes },
  { label: YesNoAnswer.No, value: YesNoAnswer.No },
];

export const UNDER_1_TO_FIVE_PLUS_OPTIONS = [
  { label: 'Under 1', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5+', value: 5 },
];

export const NONE_TO_FIVE_PLUS_OPTIONS = [
  { label: 'None', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5+', value: 5 },
];

export enum YesNoDontKnowAnswer {
  Unspecified = '',
  Yes = 'Yes',
  No = 'No',
  DontKnow = "Don't Know",
}

export const YES_NO_DONT_KNOW_OPTIONS = [
  { label: YesNoDontKnowAnswer.Yes, value: YesNoDontKnowAnswer.Yes },
  { label: YesNoDontKnowAnswer.No, value: YesNoDontKnowAnswer.No },
  { label: YesNoDontKnowAnswer.DontKnow, value: YesNoDontKnowAnswer.DontKnow },
];

export enum QueryParamKeys {
  ctype = 'ctype',
  qid = 'qid',
  pid = 'pid',
  cid = 'cid',
}

export enum FunnelType {
  Motor = 'motor',
  Home = 'home',
  Landlord = 'landlord',
  Retrieve = 'retrieve',
  Payment = 'payment',
  Outage = 'outage',
}

export enum PolicyIssued {
  Unspecified = '',
  Person = 'Person',
  Business = 'Business',
}

export interface LocationState<FlowType> {
  flow?: FlowType;
}

export interface FunnelState<FlowType, StepType> {
  flow: FlowType;
  step: StepType;
}

export interface StepProps<F, S, T extends FunnelState<F, S>> {
  funnelState: T;
  flowType: F;
  stepType: S;
}

export interface FormAnswer<FormValues, StepType> {
  direction: StepDirection;
  formValues: FormValues;
  apiResponses: Partial<unknown>;
  stepFilter?: StepType;
  targetStep?: StepType;
  autoMergeFormToState?: boolean;
}

export interface FlowAction<FormValues, StepType>
  extends FormAnswer<FormValues, StepType> {
  sourceStep: StepType;
}

export interface MoveFlowAction<FormValues, FlowType, StepType>
  extends FormAnswer<FormValues, StepType> {
  targetFlow?: FlowType;
}

// A flow value is an edge in the transition graph defined in the
// FlowDefinition. It might be a raw FlowPosition, or it might be
// function that calculates a new FlowPosition.
export type FlowValue<FormValues, F, S, T extends FunnelState<F, S>> =
  | ((q: T, a: FlowAction<FormValues, S>) => Partial<FunnelState<F, S>>)
  | Partial<FunnelState<F, S>>;

// A FlowDefinition is made up out of flowtypes and steptypes.
// This allows different flows to have the same steps, but different
// rules for transitions.
export type FlowDefinition<
  FormValues,
  F,
  S extends string,
  T extends FunnelState<F, S>
> = {
  [step in S]?: FlowValue<FormValues, F, S, T>;
};
export type AllFlowsDefinition<
  FormValues,
  F extends string,
  S extends string,
  T extends FunnelState<F, S>
> = {
  [flow in F]: FlowDefinition<FormValues, F, S, T>;
};
